import axios from 'axios'
import type { AxiosInstance, AxiosResponse } from 'axios'
import type { HYRequestInterceptors, HYRequestConfig } from './type'
import localCache from '@/utils/cache'
import { ElLoading, ElMessage } from 'element-plus'
import type { LoadingInstance } from 'element-plus/es/components/loading/src/loading'
import { saveAs } from 'file-saver'
import { tansParams, blobValidate } from "@/utils/ruoyi";

const DEAFULT_LOADING = true

class HYRequest {
  instance: AxiosInstance
  interceptors?: HYRequestInterceptors
  showLoading: boolean
  loading?: LoadingInstance

  constructor(config: HYRequestConfig) {
    // 创建axios实例
    this.instance = axios.create(config)

    // 保存基本信息
    this.showLoading = config.showLoading ?? DEAFULT_LOADING
    this.interceptors = config.interceptors

    // 使用拦截器
    // 1.从config中取出的拦截器是对应的实例的拦截器
    this.instance.interceptors.request.use(
      this.interceptors?.requestInterceptor,
      this.interceptors?.requestInterceptorCatch
    )
    this.instance.interceptors.response.use(
      this.interceptors?.responseInterceptor,
      this.interceptors?.responseInterceptorCatch
    )

    // 2.添加所有的实例都有的拦截器
    this.instance.interceptors.request.use(
      (config) => {
        if (this.showLoading) {
          this.loading = ElLoading.service({
            lock: true,
            text: '正在请求数据....',
            background: 'rgba(0, 0, 0, 0.5)'
          })
        }
        return config
      },
      (err) => {
        return err
      }
    )

    this.instance.interceptors.response.use(
      (res) => {
        // 将loading移除
        this.loading?.close()
        const data = res.data
        
          return data
      },
      (err) => {
        // 将loading移除
        this.loading?.close()

        // 例子: 判断不同的HttpErrorCode显示不同的错误信息
        if (err.response.status === 404) {
          console.log('404的错误~',err)
        }
        return err
      }
    )
  }

  request<T>(config: HYRequestConfig<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      // 1.单个请求对请求config的处理
      if (config.interceptors?.requestInterceptor) {
        config = config.interceptors.requestInterceptor(config)
      }

      // 2.判断是否需要显示loading
      if (config.showLoading === false) {
        this.showLoading = config.showLoading
      }

      this.instance
        .request<any, T>(config)
        .then((res:any) => {
          // 1.单个请求对数据的处理
          if (config.interceptors?.responseInterceptor) {
            res = config.interceptors.responseInterceptor(res)
          }
          // 2.将showLoading设置true, 这样不会影响下一个请求
          this.showLoading = DEAFULT_LOADING
          if((res.code === 401 || res.code === 500) && config.url == '/auth/login'){
            ElMessage.error(res.msg);
            console.log('清除缓存',config)
            localCache.clearCache()
            window.location.href = '/login'
          }else if(res.code === 401){
            localCache.clearCache()
            window.location.href = '/login'
          }else if(res.code === 500){
            ElMessage.error(res.msg);
            reject(res)
          }
          // 3.将结果resolve返回出去
          resolve(res)
        })
        .catch((err) => {
          // 将showLoading设置true, 这样不会影响下一个请求
          this.showLoading = DEAFULT_LOADING
          reject(err)
          return err
        })
    })
  }

  get<T>(config: HYRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: 'GET' })
  }

  post<T>(config: HYRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: 'POST' })
  }

  delete<T>(config: HYRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE' })
  }

  patch<T>(config: HYRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: 'PATCH' })
  }
  put<T>(config: HYRequestConfig<T>): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT' })
  }
  
  // 通用下载方法
  download(url: string, params: any, filename: string, config?: any): Promise<void> {
    const downloadLoadingInstance = ElLoading.service({ 
      text: "正在下载数据，请稍候", 
      spinner: "el-icon-loading", 
      background: "rgba(0, 0, 0, 0.7)" 
    });
    
    return this.instance.post(url, params, {
      transformRequest: [(params) => { return tansParams(params) }],
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'blob',
      ...config
    }).then(async (response:any) => {
      const isBlob = blobValidate(response);
      if (isBlob) {
        const blob = new Blob([response]);
        saveAs(blob, filename);
      } else {
        const blob = new Blob([response]);
        const reader = new FileReader();
        reader.readAsText(blob);
        reader.onload = () => {
          try {
            const rspObj = JSON.parse(reader.result as string);
            const errMsg = rspObj.msg || '下载失败';
            ElMessage.error(errMsg);
          } catch (error) {
            ElMessage.error('下载文件出现错误，请联系管理员！');
          }
        };
      }
      downloadLoadingInstance.close();
    }).catch((r) => {
      console.error(r);
      ElMessage.error('下载文件出现错误，请联系管理员！');
      downloadLoadingInstance.close();
    });
  }
}

export default HYRequest
